import { useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ShopContext } from "../../context/ShopContext";
import { formatSize } from "../../utilityFunction/convertUnits";
import './homepageshop.scss';
import { cardImage } from "../../utilityFunction/cardImage";

// Placeholder cards while the products load, so the page doesn't jump when they arrive.
const PLACEHOLDER_COUNT = 4;
// A computer screen's first row; these images load straight away, the rest as they scroll in.
const FIRST_ROW = 4;

const formatPrice = (amount) => `C$${Number(amount).toFixed(2)}`;

// `product` is a row from /api/homeProducts: the shop listing's fields (its default variant,
// price range, variant count...) plus `subtitle`.
function ProductCard({ product, index }) {
  const { addToCart } = useContext(ShopContext);
  const [justAdded, setJustAdded] = useState(false);
  const addedTimer = useRef(null);
  useEffect(() => () => clearTimeout(addedTimer.current), []);

  const productLink = `/DynamicShop/${product.slug}`;
  // With several sizes the customer picks one on the product page, as in the shop.
  const hasOptions = product.variantCount > 1;
  const sizeLine = hasOptions
    ? `${product.variantCount} sizes`
    : formatSize(product.size_name, product.size_value).label;
  const priceLine =
    hasOptions && product.minPrice !== product.maxPrice
      ? `From ${formatPrice(product.minPrice)}`
      : formatPrice(product.price);

  const add = () => {
    addToCart(product);
    setJustAdded(true);
    clearTimeout(addedTimer.current);
    addedTimer.current = setTimeout(() => setJustAdded(false), 1500);
  };

  return (
    <li className="hp-product">
      <Link to={productLink} className="hp-product-link">
        <div className="hp-product-image">
          <img
            src={cardImage(product.image)}
            alt=""
            loading={index < FIRST_ROW ? "eager" : "lazy"}
            fetchpriority={index === 0 ? "high" : "auto"}
            decoding="async"
            width="400"
            height="400"
          />
        </div>
        <h3 className="hp-product-name">{product.product_name}</h3>
      </Link>
      {product.subtitle && <p className="hp-product-subtitle">{product.subtitle}</p>}
      {sizeLine && <p className="hp-product-size">{sizeLine}</p>}
      <p className="hp-product-price">{priceLine}</p>
      {product.comingSoon ? (
        <button type="button" className="hp-product-button" disabled>
          Coming soon
        </button>
      ) : hasOptions ? (
        <Link to={productLink} className="hp-product-button">
          Choose size
        </Link>
      ) : (
        <button type="button" className="hp-product-button" disabled={!product.inStock} onClick={add}>
          <span aria-live="polite">{!product.inStock ? "Out of stock" : justAdded ? "Added to cart" : "Add to cart"}</span>
        </button>
      )}
    </li>
  );
}

export default function HomePageShop({ heading, products, isAdmin }) {
  // Nothing picked (or the products couldn't load): visitors don't see an empty section.
  if (products?.length === 0 && !isAdmin) return null;

  return (
    <section className="hp-section">
      <div className="hp-section-header">
        <h2 className="hp-section-title">{heading}</h2>
        <Link to="/Shop" className="hp-section-link">
          View all <span aria-hidden="true">→</span>
        </Link>
      </div>

      {products === null && (
        <ul className="hp-product-grid" aria-busy="true" aria-label="Loading products">
          {Array.from({ length: PLACEHOLDER_COUNT }, (_, index) => (
            <li key={index} className="hp-product is-placeholder" aria-hidden="true">
              <div className="hp-product-image" />
              <span className="hp-placeholder-line" />
              <span className="hp-placeholder-line short" />
            </li>
          ))}
        </ul>
      )}

      {products?.length > 0 && (
        <ul className="hp-product-grid">
          {products.map((product, index) => (
            <ProductCard key={product.shop_item_id} product={product} index={index} />
          ))}
        </ul>
      )}

      {products?.length === 0 && (
        <p className="hp-admin-note">
          No products are shown here yet, so visitors don't see this section. Use Edit home page to pick some.
        </p>
      )}
    </section>
  );
}
